import { Component, Host, h, Prop } from '@stencil/core';

export interface AhaPanel extends HTMLAhaPanelElement {}

/**
 * Creates a responsive panel that consolidates a section of content.
 *
 * By default, this element gets an ARIA role of `region`. This can be
 * customized via the `role` property. `region` is recommended for cases where
 * you want to delineate a region of content and allow screen readers to "jump"
 * to it. More specific roles may be more appropriate; see the [region role
 * documentation](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Region_role).
 *
 * @slot default - Contents of the `aha-panel` body.
 * @slot heading - Contents of the heading slot.
 */
@Component({
  tag: 'aha-panel',
  styleUrl: 'aha-panel.css',
  shadow: true,
})
export class AhaPanel {
  /**
   * The title of the panel, shown as a heading.
   */
  @Prop() heading!: string;

  /**
   * The heading level (h1, h2, h3, etc). Useful for ensuring a consistent
   * heading hierarchy.
   */
  @Prop({ reflect: true }) headingLevel: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' = 'h2';

  /** Whether or not the panel should have a subtle drop shadow. */
  @Prop({ reflect: true }) boxShadow = false;

  /**
   * The ARIA role assigned to the container. By default, the container has a
   * role of `region`, which implies that it is a crucial part of the document
   * and allows screen readers to jump to it.
   */
  @Prop({ reflect: true }) role = 'region';

  /**
   * Whether or not to disable the header. This will hide the header and remove
   * the top padding.
   */
  @Prop() disableHeader = false;

  render() {
    return (
      <Host>
        <div role={this.role} aria-labelledby="heading">
          {this.disableHeader ? null : (
            <header id="header">
              <slot name="heading">{h(this.headingLevel, { id: 'heading' }, this.heading)}</slot>
            </header>
          )}
          <div id="main">
            <slot></slot>
          </div>
        </div>
      </Host>
    );
  }
}
